import React, { useMemo, useState } from 'react'
import { Badge, Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap'
import { formatReadableDate } from '../../../utils/formatDate'
import ImageViewer from '../../../components/Common/ImageViewer/ImageViewer'
import { useQuotation } from '../../../hooks/useQuotation'
import QuotationDetailsCard from '../../QuotationPages/QuotationDetailsCard'

const ACTION_STYLES = {
  create: { color: 'success', icon: 'mdi-plus-circle', label: 'Created' },
  update: { color: 'info', icon: 'mdi-pencil-circle', label: 'Updated' },
  delete: { color: 'danger', icon: 'mdi-delete-circle', label: 'Deleted' },
  restore: { color: 'warning', icon: 'mdi-restore', label: 'Restored' },
  login: { color: 'primary', icon: 'mdi-login', label: 'Login' },
  logout: { color: 'secondary', icon: 'mdi-logout', label: 'Logout' },
  grant_access: { color: 'success', icon: 'mdi-account-key', label: 'Access Granted' },
  revoke_access: { color: 'danger', icon: 'mdi-account-off', label: 'Access Revoked' },
  update_roles: { color: 'info', icon: 'mdi-shield-account', label: 'Roles Updated' },
  reset_password: { color: 'warning', icon: 'mdi-lock-reset', label: 'Password Reset' },
  impersonate: { color: 'dark', icon: 'mdi-account-switch', label: 'Impersonated' },
}

const isFileCollection = (value) =>
  Array.isArray(value) &&
  value.every(item =>
    item &&
    typeof item === 'object' &&
    (
      Object.prototype.hasOwnProperty.call(item, 'url') ||
      Object.prototype.hasOwnProperty.call(item, 'path') ||
      Object.prototype.hasOwnProperty.call(item, 'name')
      )
  )

const parseJsonLikeValue = (value) => {
  if (typeof value !== 'string') return value

  const trimmed = value.trim()

  if (!trimmed) return value

  if (
    (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
    (trimmed.startsWith('{') && trimmed.endsWith('}'))
  ) {
    try {
      return JSON.parse(trimmed)
    } catch {
      return value
    }
  }

  return value
}

const summarizeFileCollection = (items = [], fallbackLabel = 'files') => {
  const count = items.length

  if (count === 0) return `No ${fallbackLabel}`
  if (count === 1) return `1 ${fallbackLabel.slice(0, -1)}`

  return `${count} ${fallbackLabel}`
}

const getFileCollectionSummary = (key, items = []) => {
  if (key === 'images') return summarizeFileCollection(items, 'images uploaded')
  if (key === 'documents') return summarizeFileCollection(items, 'documents attached')
  if (key === 'auction_sheet') return summarizeFileCollection(items, 'auction sheet files')
  if (key === 'true_report') return summarizeFileCollection(items, 'report files')

  return summarizeFileCollection(items)
}

const buildPreviewItems = (items = [], maxVisible = 3) => {
  if (!Array.isArray(items) || items.length === 0) return []

  if (items.length <= maxVisible) {
    return items.map((item, index) => ({
      item,
      index,
      extraCount: 0,
      isOverflow: false,
    }))
  }

  const visibleItems = items.slice(0, maxVisible)

  return visibleItems.map((item, index) => ({
    item,
    index,
    extraCount: index === maxVisible - 1 ? items.length - maxVisible : 0,
    isOverflow: index === maxVisible - 1,
  }))
}

const formatFieldLabel = (value = '') => String(value)
  .replace(/_/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/\b\w/g, char => char.toUpperCase())

const getObjectSummary = (key, value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return 'N/A'

  const preferredKeys = ['label', 'name', 'title', 'username', 'email', 'phone', 'short_name', 'value']

  for (const preferredKey of preferredKeys) {
    const preferredValue = value[preferredKey]

    if (preferredValue !== null && preferredValue !== undefined && preferredValue !== '') {
      if (preferredKey === 'value' && typeof preferredValue === 'object') {
        break
      }

      if (Object.prototype.hasOwnProperty.call(value, 'id') && preferredKey !== 'label' && preferredKey !== 'value') {
        const id = value.id

        if (id !== null && id !== undefined && String(id) !== String(preferredValue)) {
          return `${preferredValue} (#${id})`
        }
      }

      return String(preferredValue)
    }
  }

  const hiddenKeys = new Set(['url', 'path', 'pivot', 'created_at', 'updated_at', 'deleted_at'])
  const summaryParts = Object.entries(value)
    .filter(([childKey, childValue]) => !hiddenKeys.has(childKey) && childValue !== null && childValue !== undefined && childValue !== '')
    .slice(0, 3)
    .map(([childKey, childValue]) => `${formatFieldLabel(childKey)}: ${formatFieldValue(childKey, childValue)}`)

  if (summaryParts.length === 0) {
    return key ? `${formatFieldLabel(key)} updated` : 'Details updated'
  }

  return summaryParts.join(', ')
}

const formatFieldValue = (key, value) => {
  value = parseJsonLikeValue(value)

  if (value === null || value === undefined || value === '') return 'N/A'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (Array.isArray(value)) {
    if (isFileCollection(value)) {
      return getFileCollectionSummary(key, value)
    }

    const items = value
      .map(item => formatFieldValue(key, item))
      .filter(item => item && item !== 'N/A')

    return items.length > 0 ? items.join(', ') : 'N/A'
  }
  if (
    typeof value === 'object' &&
    value !== null &&
    Object.prototype.hasOwnProperty.call(value, 'id') &&
    Object.prototype.hasOwnProperty.call(value, 'label')
  ) {
    const label = value.label ?? ''
    const id = value.id

    if (label === '' || label === null || label === undefined) {
      return id ?? 'N/A'
    }

    if (id === null || id === undefined || String(id) === String(label)) {
      return String(label)
    }

    return `${label} (#${id})`
  }
  if (typeof value === 'object' && value !== null) {
    if (Object.prototype.hasOwnProperty.call(value, 'url')) {
      return 'File attached'
    }

    return getObjectSummary(key, value)
  }

  return String(value)
}

const FieldValueContent = ({ fieldKey, value, tone = 'default', onPreviewImage = () => {} }) => {
  value = parseJsonLikeValue(value)

  const colorClass =
    tone === 'danger' ? 'text-danger' : tone === 'success' ? 'text-success' : 'text-dark'

  if (Array.isArray(value) && isFileCollection(value)) {
    return (
      <div>
        <div className={colorClass}>{getFileCollectionSummary(fieldKey, value)}</div>
        {fieldKey === 'images' ? (
          <div className="d-flex flex-wrap gap-2 mt-2">
            {buildPreviewItems(value).map(({ item, index, extraCount, isOverflow }) => {
              const imageUrl = item?.url || item?.path

              if (!imageUrl) return null

              return (
                <button
                  key={item?.id || imageUrl || index}
                  type="button"
                  onClick={() => onPreviewImage(value, index, `Activity Images (${index + 1}/${value.length})`)}
                  className="btn p-0 border rounded overflow-hidden position-relative bg-white"
                  style={{ width: 72, height: 72 }}
                  title={`Preview image ${index + 1}`}
                >
                  <img
                    src={imageUrl}
                    alt={`Preview ${index + 1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  {item?.isPrimary ? (
                    <span
                      className="position-absolute top-0 start-0 badge bg-primary"
                      style={{ fontSize: 9, borderRadius: 0 }}
                    >
                      Primary
                    </span>
                  ) : null}
                  {isOverflow && extraCount > 0 ? (
                    <span
                      className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center text-white fw-bold"
                      style={{ background: 'rgba(0, 0, 0, 0.55)', fontSize: 18 }}
                    >
                      +{extraCount}
                    </span>
                  ) : null}
                </button>
              )
            })}
          </div>
        ) : null}
      </div>
    )
  }

  const formatted = formatFieldValue(fieldKey, value)

  return <span className={colorClass}>{formatted}</span>
}

const SectionTitle = ({ icon, children }) => (
  <div className="fw-semibold mb-2">
    <i className={`mdi ${icon} me-2 text-primary`} />
    {children}
  </div>
)

const InfoRow = ({ label, value }) => (
  <div className="small mb-1">
    <strong>{label}:</strong> {value}
  </div>
)

const getCombinedKeys = (...sources) => (
  Array.from(
    new Set(
      sources.flatMap(source => (
        source && typeof source === 'object' && !Array.isArray(source)
          ? Object.keys(source)
          : []
      ))
    )
  )
)

const isQuotationCreatedActivity = (activityLog) => {
  if (activityLog?.action !== 'create') return false

  const subjectModel = (activityLog?.subject_type || '')
    .split('\\')
    .pop()
    .toLowerCase()

  return subjectModel === 'quotation'
}

const ChangeTable = ({
  oldValues = {},
  newValues = {},
  oldDisplay = {},
  newDisplay = {},
  fieldLabels = {},
  onPreviewImage = () => {},
}) => {
  const keys = getCombinedKeys(oldValues, newValues, oldDisplay, newDisplay)

  if (keys.length === 0) return null

  return (
    <div className="border rounded overflow-hidden mb-3">
      <div className="d-flex bg-light border-bottom px-3 py-2 fw-semibold small">
        <div style={{ width: '28%' }}>Field</div>
        <div style={{ width: '36%' }} className="text-danger">Before</div>
        <div style={{ width: '36%' }} className="text-success">After</div>
      </div>
      {keys.map((key, index) => (
        <div
          key={key}
          className={`d-flex px-3 py-2 small ${index !== keys.length - 1 ? 'border-bottom' : ''} ${index % 2 ? 'bg-light' : ''}`}
        >
          <div style={{ width: '28%' }} className="fw-semibold text-dark">
            {fieldLabels[key] || key}
          </div>
          <div style={{ width: '36%' }} className="pe-2">
            <FieldValueContent
              fieldKey={key}
              value={oldDisplay[key] ?? oldValues[key]}
              tone="danger"
              onPreviewImage={onPreviewImage}
            />
          </div>
          <div style={{ width: '36%' }}>
            <FieldValueContent
              fieldKey={key}
              value={newDisplay[key] ?? newValues[key]}
              tone="success"
              onPreviewImage={onPreviewImage}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

const ValueList = ({
  values = {},
  displayValues = {},
  fieldLabels = {},
  emptyLabel = 'No details available',
  onPreviewImage = () => {},
}) => {
  const keys = getCombinedKeys(values, displayValues)

  if (keys.length === 0) {
    return <div className="small text-muted">{emptyLabel}</div>
  }

  return (
    <div className="border rounded overflow-hidden mb-3">
      {keys.map((key, index) => (
        <div
          key={key}
          className={`d-flex px-3 py-2 small ${index !== keys.length - 1 ? 'border-bottom' : ''} ${index % 2 ? 'bg-light' : ''}`}
        >
          <div style={{ width: '32%' }} className="fw-semibold text-dark">
            {fieldLabels[key] || key}
          </div>
          <div style={{ width: '68%' }}>
            <FieldValueContent
              fieldKey={key}
              value={displayValues[key] ?? values[key]}
              onPreviewImage={onPreviewImage}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

const ActivityLogView = ({ isOpen, toggle, activityLog, isLoading }) => {
  const [viewerOpen, setViewerOpen] = useState(false)
  const [viewerImages, setViewerImages] = useState([])
  const [viewerIndex, setViewerIndex] = useState(0)
  const [viewerTitle, setViewerTitle] = useState('Activity Images')

  const config = ACTION_STYLES[activityLog?.action] || { color: 'secondary', icon: 'mdi-history', label: activityLog?.action }
  const fieldLabels = activityLog?.field_labels || {}
  const oldValues = activityLog?.old_values || {}
  const newValues = activityLog?.new_values || {}
  const oldDisplay = activityLog?.old_display || {}
  const newDisplay = activityLog?.new_display || {}
  const shouldShowQuotationView = isQuotationCreatedActivity(activityLog)
  const quotationIdentifier = activityLog?.subject_id || newValues?.id || newDisplay?.id
  const { data: quotationResponse, isLoading: isQuotationLoading } = useQuotation(
    quotationIdentifier,
    Boolean(isOpen && shouldShowQuotationView && quotationIdentifier)
  )
  const quotation = quotationResponse?.data ?? {}
  const imageViewerImages = useMemo(
    () => viewerImages
      .map(item => item?.url || item?.path)
      .filter(Boolean),
    [viewerImages]
  )

  const openImagePreview = (items = [], initialIndex = 0, title = 'Activity Images') => {
    setViewerImages(Array.isArray(items) ? items : [])
    setViewerIndex(initialIndex)
    setViewerTitle(title)
    setViewerOpen(true)
  }

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="lg" centered scrollable>
        <ModalHeader toggle={toggle}>
          Activity Log {activityLog?.id ? `#${activityLog.id}` : ''}
        </ModalHeader>
        <ModalBody>
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner size="sm" color="primary" />
            </div>
          ) : activityLog ? (
            <>
              <div className="border rounded p-3 mb-3">
                <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                  <Badge color={config.color}>
                    <i className={`mdi ${config.icon} me-1`} />
                    {config.label}
                  </Badge>
                  <Badge color="light" className="text-dark text-uppercase">
                    {activityLog.module}
                  </Badge>
                </div>

                <div className="small text-muted mb-2">
                  {activityLog.description || 'No description'}
                </div>

                <InfoRow
                  label="User"
                  value={activityLog.causer_user_display || activityLog.causer_user_snapshot?.label || activityLog.user?.name || activityLog.user?.username || 'System'}
                />
                <InfoRow
                  label="Subject"
                  value={`${activityLog.subject_label || activityLog.subject_type || 'N/A'}${activityLog.subject_id ? ` (#${activityLog.subject_id})` : ''}`}
                />
                <InfoRow
                  label="Showroom"
                  value={activityLog.showroom_display || activityLog.showroom_snapshot?.label || activityLog.showroom?.name || activityLog.showroom?.short_name || 'N/A'}
                />
                <InfoRow label="Time" value={formatReadableDate(activityLog.created_at)} />
                <InfoRow label="IP Address" value={activityLog.ip_address || 'N/A'} />
              </div>

              {activityLog.action === 'update' && (
                <>
                  <SectionTitle icon="mdi-swap-horizontal">What Changed</SectionTitle>
                  <ChangeTable
                    oldValues={oldValues}
                    newValues={newValues}
                    oldDisplay={oldDisplay}
                    newDisplay={newDisplay}
                    fieldLabels={fieldLabels}
                    onPreviewImage={openImagePreview}
                  />
                </>
              )}

              {(activityLog.action === 'create' || activityLog.action === 'restore') && (
                <>
                  <SectionTitle icon="mdi-file-document-outline">Saved Details</SectionTitle>
                  {shouldShowQuotationView && quotationIdentifier ? (
                    <QuotationDetailsCard quotation={quotation} isLoading={isQuotationLoading} />
                  ) : (
                    <ValueList
                      values={newValues}
                      displayValues={newDisplay}
                      fieldLabels={fieldLabels}
                      onPreviewImage={openImagePreview}
                    />
                  )}
                </>
              )}

              {activityLog.action === 'delete' && (
                <>
                  <SectionTitle icon="mdi-delete-outline">Deleted Details</SectionTitle>
                  <ValueList
                    values={oldValues}
                    displayValues={oldDisplay}
                    fieldLabels={fieldLabels}
                    onPreviewImage={openImagePreview}
                  />
                </>
              )}

              {activityLog.metadata && Object.keys(activityLog.metadata).length > 0 && (
                <>
                  <SectionTitle icon="mdi-information-outline">Extra Information</SectionTitle>
                  <ValueList
                    values={activityLog.metadata}
                    displayValues={activityLog.metadata}
                    fieldLabels={{}}
                    onPreviewImage={openImagePreview}
                  />
                </>
              )}
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" size="sm" outline onClick={toggle}>Close</Button>
        </ModalFooter>
      </Modal>

      <ImageViewer
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        images={imageViewerImages}
        initialIndex={viewerIndex}
        title={viewerTitle}
      />
    </>
  )
}

export default ActivityLogView

